// client/src/pages/Features.js
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
});

export default function Features() {
  const coreFeatures = [
    {
      title: "Automation Engine",
      desc: "Auto-sync, status checks and routing so your orders run 24/7 without micro-management.",
    },
    {
      title: "Multi-Provider Ready",
      desc: "Connect multiple SMM providers and route orders based on speed, price or reliability.",
    },
    {
      title: "Smart Order Management",
      desc: "Mass orders, drip-feed and refill handling in a clean, easy-to-use dashboard.",
    },
    {
      title: "Real-Time Tracking",
      desc: "Track every order with live statuses, average time and detailed logs.",
    },
    {
      title: "Reseller API",
      desc: "Offer your own panel using smsorbit as the backend through a simple JSON API.",
    },
    {
      title: "Tickets & Support",
      desc: "Built-in ticket system so your users can reach you directly from the panel.",
    },
  ];

  const highlightBullets = [
    "User Friendly Dashboard",
    "Secure & Safe – Your data is fully protected",
    "High Quality Results – Real and long-lasting",
  ];

  return (
    <div className="min-h-screen bg-[#05040f] text-white px-6 md:px-20 py-16">
      <motion.div
        {...fadeUp(0.05)}
        className="max-w-4xl mx-auto text-center mb-12"
      >
        <Link to="/" className="inline-flex items-center justify-center gap-3">
          <img src="/logo.png" alt="smsorbit" className="w-10 h-10 rounded-xl" />
          <span className="text-xl font-bold tracking-wide">SMSOrbit</span>
        </Link>
        <h1 className="mt-6 text-3xl md:text-4xl font-bold">
          SMSOrbit <span className="text-purple-400">Features</span>
        </h1>
        <p className="mt-3 text-gray-400 text-sm md:text-base max-w-2xl mx-auto">
          Everything you need to run a modern SMM business – from orders and
          payments to routing and API access.
        </p>
      </motion.div>

      <motion.div
        {...fadeUp(0.15)}
        className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8 mb-12"
      >
        {coreFeatures.map((f) => (
          <motion.div
            key={f.title}
            whileHover={{ y: -4, scale: 1.01 }}
            className="rounded-3xl bg-[#090912] border border-white/5 p-7 text-sm md:text-base"
          >
            <h3 className="font-semibold mb-2 text-lg">{f.title}</h3>
            <p className="text-gray-300">{f.desc}</p>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        {...fadeUp(0.2)}
        className="max-w-3xl mx-auto rounded-3xl bg-[#090912] border border-purple-500/40 p-6"
      >
        <h2 className="text-lg md:text-xl font-semibold mb-3 text-center">
          Why users love <span className="text-purple-400">SMSOrbit</span>
        </h2>
        <ul className="space-y-2 text-sm md:text-base text-gray-200">
          {highlightBullets.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <span className="mt-[5px] text-yellow-300">🌟</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </motion.div>
    </div>
  );
}
